import type { DashboardNotificationItem, DashboardNotificationSeverity } from '../../types';
import { cn } from '../../utils/cn';
import { NOTIFICATION_SEVERITIES, severityDots, severityLabels } from './notificationMeta';

// The tab to open first: the highest priority with something unread, else the highest with anything.
export function defaultSeverity(items: DashboardNotificationItem[]): DashboardNotificationSeverity {
    return NOTIFICATION_SEVERITIES.find(severity => items.some(item => item.severity === severity && !item.read))
        ?? NOTIFICATION_SEVERITIES.find(severity => items.some(item => item.severity === severity))
        ?? 'high';
}

interface SeverityTabsProps {
    items: DashboardNotificationItem[];
    active: DashboardNotificationSeverity;
    onChange: (severity: DashboardNotificationSeverity) => void;
    className?: string;
}

// Cao / Trung bình / Thấp side by side, each with its count and, in red, how many are unread.
export function SeverityTabs({ items, active, onChange, className }: SeverityTabsProps) {
    return (
        <div role="tablist" aria-label="Mức độ" className={cn('flex gap-1 border-b border-ink-200', className)}>
            {NOTIFICATION_SEVERITIES.map(severity => {
                const ofSeverity = items.filter(item => item.severity === severity);
                const unread = ofSeverity.filter(item => !item.read).length;
                const selected = severity === active;
                return (
                    <button
                        key={severity}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        disabled={!ofSeverity.length}
                        onClick={() => onChange(severity)}
                        className={cn(
                            '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors focus:outline-none focus-visible:bg-academy-50 disabled:cursor-default disabled:opacity-40',
                            selected ? 'border-academy-600 font-semibold text-academy-800' : 'border-transparent text-ink-600 enabled:hover:text-ink-900',
                        )}
                    >
                        <span className={cn('h-2 w-2 rounded-full', severityDots[severity])} />
                        {severityLabels[severity]}
                        <span className="tabular-nums text-ink-500">{ofSeverity.length}</span>
                        {unread ? (
                            <span className="rounded-full bg-danger-600 px-1.5 text-[11px] font-semibold leading-4 tabular-nums text-white" aria-label={`${unread} chưa xem`}>
                                {unread}
                            </span>
                        ) : null}
                    </button>
                );
            })}
        </div>
    );
}
